import React, { useState, useEffect } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGlobalSlice } from "app/slice";
import { VleMobileAccount } from "./vle-mobile-account";
import { MobileAccountDialog } from "app/components/parts/mobile-account-dialog";
import {
  getSingleNetworkSelectionData,
  saveSingleNetworkSelectionData,
} from "utils/purchase-flow-storage";

import { Label } from "app/components/ui/label";
import { Checkbox } from "app/components/ui/checkbox";
import { Input } from "app/components/ui/input";
import { Button } from "app/components/ui/button";
import { Icons } from "app/components/ui/icons";
import { toast } from "app/components/ui/use-toast";

interface OutletContext {
  isMobileAccountView: boolean;
  setIsMobileAccountView: React.Dispatch<React.SetStateAction<boolean>>;
}

type FormData = {
  selection: number;
  email: string;
  password: string;
};

const selectionOptions = ["I am a new customer", "I have an account."];

export const MakeASelection = () => {
  const { useVerifyCustomerMutation, actions } = useGlobalSlice();
  const { isMobileAccountView, setIsMobileAccountView } =
    useOutletContext<OutletContext>();

  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [showMobileAccountDialog, setShowMobileAccountDialog] = useState(false);
  const [
    verify,
    {
      isLoading: verifyLoading,
      isSuccess: verifySuccess,
      isError: verifyError,
      error: verifyErrorMessage,
      data: verifyData,
    },
  ] = useVerifyCustomerMutation();

  const navigate = useNavigate();
  const savedData = getSingleNetworkSelectionData();
  const form = useForm<FormData>({
    defaultValues: {
      selection: savedData?.makeASelection?.selection ?? undefined,
      email: savedData?.makeASelection?.email || "",
      password: savedData?.makeASelection?.password || "",
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const selectedIndex = watch("selection");

  const handleChange = (index: number) => {
    form.reset({
      selection: index,
      email: "",
      password: "",
    });
    setIsMobileAccountView(false);
    saveSingleNetworkSelectionData({
      makeASelection: {
        selection: index,
        email: "",
        password: "",
      },
    });
  };

  // Save to sessionStorage when form values change
  useEffect(() => {
    const subscription = watch((value) => {
      saveSingleNetworkSelectionData({
        makeASelection: {
          selection: value.selection,
          email: value.email || "",
          password: value.password || "",
        },
      });
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey) {
      return;
    }

    if (
      [
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ].includes(e.key)
    ) {
      return;
    }

    if (!/\d/.test(e.key)) {
      e.preventDefault();
    }
  };

  const onSubmit = (data: FormData) => {
    if (selectedIndex === undefined || selectedIndex === null) {
      toast({
        title: "Selection Required",
        description: "Please select an option",
        variant: "error",
      });
      return;
    }

    if (selectedIndex === 0) {
      navigate("/single-network-phone-type");
    } else {
      // call the api and then navigate
      const body = { email: data.email, password: data.password };
      verify(body);
    }
  };

  useEffect(() => {
    if (verifySuccess && selectedIndex === 1 && verifyData?.data) {
      // Validate network type before proceeding
      const userNetworkType =
        verifyData?.data?.networkType ||
        verifyData?.data?.customer?.networkType;
      const activeLines = verifyData?.data?.Activeline || 0;

      // Single Network flow expects networkType = 1
      if (userNetworkType === 2) {
        toast({
          title: "Network Type Mismatch",
          description:
            "You are a Multi Network user. This is for Single Network users only. Please use the Multi Network selection instead.",
          variant: "error",
        });
        return;
      }

      // If number of lines is 0 and network type is not 1, continue with network plan flow
      if (activeLines === 0 && userNetworkType !== 1) {
        dispatch(
          actions.setUser({
            user: {
              ...verifyData?.data,
              ...verifyData?.data?.customer,
              ...verifyData?.data?.planData,
              _id: verifyData?.data?.customerId,
            },
            rememberDevice: false,
          })
        );
        navigate("/single-network-phone-type");
        return;
      }

      // Show mobile account dialog instead of setting isMobileAccountView
      // Mark that user was just verified to prevent auto-redirect
      sessionStorage.setItem("userJustVerified", "true");
      setShowMobileAccountDialog(true);
      dispatch(
        actions.setUser({
          user: {
            ...verifyData?.data,
            ...verifyData?.data?.customer,
            ...verifyData?.data?.planData,
            _id: verifyData?.data?.customerId,
          },
          rememberDevice: false,
        })
      );
    }
  }, [verifySuccess, selectedIndex, verifyData, dispatch, actions, navigate]);

  useEffect(() => {
    if (verifyData?.data?.Activeline === 1) {
      toast({
        title: "Lines Limit",
        description: "You have used your one available line.",
        variant: "error",
      });
    }
  }, [verifyData?.data?.Activeline]);

  useEffect(() => {
    if (verifyError && verifyErrorMessage) {
      toast({
        title: "Verification Failed",
        description: verifyErrorMessage as string,
        variant: "error",
      });
    }
  }, [verifyError, verifyErrorMessage]);

  return (
    <FormProvider {...form}>
      <div className="w-full max-w-[31rem] md:mx-auto">
        <form className="w-full" onSubmit={handleSubmit(onSubmit)}>
          <React.Fragment>
            <div className="bg-white p-4 pb-5 sm:p-5 sm:pb-6 lg:p-6 lg:pb-[2.125rem] border border-[#E5E7EB] rounded-xl md:rounded-2xl mb-4 sm:mb-5 lg:mb-6">
              <h4 className="mb-4 text-xl font-bold leading-5 text-black sm:text-[1.35rem] lg:text-2xl sm:leading-6 lg:leading-7 font-mobile sm:mb-6 lg:mb-9">
                Make a Selection
              </h4>
              <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
                {selectionOptions.map((label, index) => {
                  const id = `selection-${index}`;
                  return (
                    <div
                      key={id}
                      className="space-y-5 sm:space-y-6 lg:space-y-[1.875rem]"
                    >
                      <div className="flex items-center gap-2 sm:gap-[0.625rem]">
                        <Checkbox
                          id={id}
                          checked={selectedIndex === index}
                          onCheckedChange={() => handleChange(index)}
                          className="w-5 h-5 md:w-[1.35rem] md:h-[1.35rem] lg:w-6 lg:h-6 rounded-[0.25rem] border-[#1D1D1B]"
                        />
                        <Label
                          htmlFor={id}
                          className="text-[#1D1D1B] text-sm md:text-[0.935rem] lg:text-base font-semibold leading-5 md:leading-6 mb-0 cursor-pointer"
                        >
                          {label}
                        </Label>
                      </div>
                      {index === 1 && selectedIndex === 1 && (
                        <div className="space-y-4 sm:space-y-[1.125rem] lg:space-y-5">
                          <div className="">
                            <Label
                              htmlFor="email"
                              className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                            >
                              Enter your email address
                            </Label>
                            <Input
                              id="email"
                              type="email"
                              inputMode="email"
                              {...register("email", {
                                required: "Email is required!",
                                pattern: {
                                  value:
                                    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                  message: "Please enter a valid email address",
                                },
                              })}
                              className="bg-white h-10 md:h-[2.875rem] p-2 md:p-[0.78125rem] md:pb-[0.84375rem] border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                            />
                            {errors.email && (
                              <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                                <span className="font-bold">*</span>
                                {errors.email?.message as string}
                              </p>
                            )}
                          </div>
                          <div className="">
                            <Label
                              htmlFor="password"
                              className="w-fit text-black text-xs sm:text-[0.825rem] md:text-sm font-medium leading-4 md:leading-5 mb-1 md:mb-[0.375rem]"
                            >
                              Enter Password
                            </Label>
                            <div className="relative">
                              <Input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                {...register("password", {
                                  required: "Password is required!",
                                })}
                                className="bg-white h-10 md:h-[2.875rem] p-2 pr-7 md:p-[0.78125rem] md:pr-9 md:pb-[0.84375rem] border-[#D1D5DB] rounded-xl text-black text-[0.825rem] sm:text-sm font-medium leading-4 md:leading-5 focus-visible:outline focus:outline-[#707C8F]"
                                maxLength={14}
                              />
                              {watch("password") && (
                                <div className="absolute top-1/2 -translate-y-1/2 right-3 md:right-[0.875rem] w-4 md:w-[1.125rem] h-4 md:h-[1.125rem] cursor-pointer">
                                  <img
                                    src={
                                      showPassword
                                        ? "/assets/eye-close.svg"
                                        : "/assets/eye-open.svg"
                                    }
                                    className="w-full h-auto"
                                    onClick={() =>
                                      setShowPassword(!showPassword)
                                    }
                                    alt={showPassword ? "Hide" : "Show"}
                                  />
                                </div>
                              )}
                            </div>
                            {errors.password && (
                              <p className="text-[#D80027] text-[0.72rem] sm:text-xs font-normal leading-5 mt-0.5 sm:mt-1">
                                <span className="font-bold">*</span>
                                {errors.password?.message as string}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {selectedIndex !== undefined && selectedIndex !== null && (
              <Button type="submit" variant="primary" disabled={verifyLoading}>
                {verifyLoading ? (
                  <div className="flex items-center gap-0.5 sm:gap-1">
                    <Icons.Spinner className="w-4 h-4 animate-spin md:w-5 md:h-5" />
                    <span>Verifying...</span>
                  </div>
                ) : (
                  "Continue"
                )}
              </Button>
            )}
          </React.Fragment>
        </form>
      </div>

      {/* Mobile Account Dialog */}
      <MobileAccountDialog
        isOpen={showMobileAccountDialog}
        onClose={() => {
          sessionStorage.removeItem("userJustVerified");
          setShowMobileAccountDialog(false);
        }}
        networkType={1}
        verifyData={verifyData}
        verifyLoading={verifyLoading}
      />
    </FormProvider>
  );
};
